import React, { useMemo } from 'react';

const formatDuration = durationMs => {
  if (typeof durationMs !== 'number' || !Number.isFinite(durationMs) || durationMs <= 0) {
    return '<1s';
  }
  const totalSeconds = Math.floor(durationMs / 1000);
  if (totalSeconds < 1) return '<1s';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    if (minutes > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${hours}h`;
  }
  if (minutes > 0) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  return `${seconds}s`;
};

export default function OverviewTab({
  stats,
  pageVisitDefinitions = [],
  pageVisitTimeframes = [],
  pageVisitSummary = {},
  pageVisitRanges = {},
  pageVisitLoading = false,
  pageVisitError = '',
}) {
  const numberFormatter = useMemo(() => new Intl.NumberFormat('es-MX'), []);

  const visitRows = useMemo(() => {
    if (!Array.isArray(pageVisitDefinitions)) return [];
    return pageVisitDefinitions.map(definition => {
      const metrics = Array.isArray(pageVisitTimeframes)
        ? pageVisitTimeframes.map(frame => {
            const summaryEntry =
              pageVisitSummary?.[frame.key]?.[definition.key] || {
                total: 0,
                registered: 0,
                guests: 0,
                averageDurationMs: 0,
              };

            return {
              key: frame.key,
              total: Number(summaryEntry.total) || 0,
              registered: Number(summaryEntry.registered) || 0,
              guests: Number(summaryEntry.guests) || 0,
              averageDurationMs: Number(summaryEntry.averageDurationMs) || 0,
            };
          })
        : [];

      return {
        key: definition.key,
        label: definition.label,
        description: definition.description,
        metrics,
      };
    });
  }, [pageVisitDefinitions, pageVisitTimeframes, pageVisitSummary]);

  const hasVisitData =
    visitRows.length > 0 &&
    visitRows.some(row => row.metrics.some(metric => metric.total > 0));

  const lastUpdatedLabel = useMemo(() => {
    const latestDate = pageVisitRanges?.daily?.until || pageVisitRanges?.weekly?.until;
    if (!(latestDate instanceof Date) || Number.isNaN(latestDate.getTime())) {
      return '';
    }
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(latestDate);
  }, [pageVisitRanges]);

  return (
    <>
      <section className="admin-stats">
        {stats.map(item => (
          <article key={item.title} className="admin-stat-card">
            <h3>{item.title}</h3>
            <strong>{item.value}</strong>
            <span>{item.detail}</span>
          </article>
        ))}
      </section>

      <section className="admin-panel">
        <div className="admin-panel-card wide">
          <h2>Visitas a páginas públicas</h2>
          <p className="admin-helper-text">
            Ventanas móviles de 24 horas, 7 días, 30 días y 12 meses. Separado por visitantes
            registrados e invitados.
            {lastUpdatedLabel ? ` Última actualización: ${lastUpdatedLabel}.` : ''}
          </p>

          {pageVisitLoading ? (
            <p>Cargando visitas...</p>
          ) : pageVisitError ? (
            <div className="admin-alert error">{pageVisitError}</div>
          ) : visitRows.length === 0 ? (
            <p>No hay páginas configuradas para el seguimiento de visitas.</p>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table compact admin-page-visits-table">
                <thead>
                  <tr>
                    <th>Página</th>
                    {pageVisitTimeframes.map(frame => (
                      <th key={frame.key} title={frame.description}>
                        {frame.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visitRows.map(row => (
                    <tr key={row.key}>
                      <td>
                        <div className="admin-visit-page">
                          <strong>{row.label}</strong>
                          {row.description ? <span>{row.description}</span> : null}
                        </div>
                      </td>
                      {row.metrics.map(metric => (
                        <td key={`${row.key}-${metric.key}`}>
                          <div className="admin-visit-metric">
                            <strong>{numberFormatter.format(metric.total)}</strong>
                            <span>{numberFormatter.format(metric.registered)} registrados</span>
                            <span>{numberFormatter.format(metric.guests)} invitados</span>
                            <span>Tiempo prom.: {formatDuration(metric.averageDurationMs)}</span>
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!pageVisitLoading && !pageVisitError && !hasVisitData ? (
            <p className="admin-helper-text">
              Aún no se registran visitas en los periodos examinados.
            </p>
          ) : null}
        </div>
      </section>
    </>
  );
}
